/** Roving keyboard focus for scheduled tasks inside the calendar region. */
import { useCallback, useEffect, useMemo, useState } from 'react';

const ARROW_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

/**
 * @param {{ id: string }[]} navigableTasks — tasks sorted for keyboard traversal
 */
export function useCalendarTaskRoving(navigableTasks) {
  const taskIds = useMemo(
    () => navigableTasks.map((task) => task.id).filter(Boolean),
    [navigableTasks],
  );
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (taskIds.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex((index) =>
      index < 0 ? index : Math.min(index, taskIds.length - 1),
    );
  }, [taskIds]);

  const onFocus = useCallback(() => {
    if (taskIds.length > 0 && activeIndex < 0) setActiveIndex(0);
  }, [activeIndex, taskIds.length]);

  const onKeyDown = useCallback(
    (event) => {
      if (!ARROW_KEYS.has(event.key) || taskIds.length === 0) return;

      event.preventDefault();
      if (activeIndex < 0) {
        setActiveIndex(0);
        return;
      }

      const delta =
        event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
      setActiveIndex((index) =>
        Math.max(0, Math.min(taskIds.length - 1, index + delta)),
      );
    },
    [activeIndex, taskIds],
  );

  const activeTaskId = activeIndex >= 0 ? taskIds[activeIndex] : null;
  const ariaActiveDescendant = activeTaskId
    ? `calendar-scheduled-task-${activeTaskId}`
    : undefined;

  return {
    activeTaskId,
    ariaActiveDescendant,
    onFocus,
    onKeyDown,
  };
}
